import { elearningAPI } from '../../services/elearningApi';
import { eventAPI } from '../../services/eventApi';
import { hubAPI } from '../../services/hubApi';
import { programAPI } from '../../services/programApi';
import { wordpressAPI } from '../../services/wordpressApi';
import { Course } from '../../types/elearning';
import { AppEvent } from '../../types/event';
import { Hub } from '../../types/hub';
import { Program } from '../../types/program';
import { WPMedia, WPPost } from '../../types/wordpress';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { isUnderRevision } from '../../utils/resourceRevision';

export const PROGRAM_TITLE = 'Massive Hemorrhage Protocol';
export const BLOODY_EASY_TITLE = 'Bloody Easy E-Tools & Publications';
export const HUB_TITLE = 'Education Hub';
export const PROGRAM_HUB_TITLE = 'eLearning';
export const RESOURCE_TITLE = 'Provincial Massive Hemorrhage Toolkit';
export const UPCOMING_TITLE = 'University of Toronto Transfusion Medicine Rounds- October 22, 2026';
export const PAST_MARK = 'September 24, 2026';
export const COURSE_TITLE = 'Transporting Blood Internally';

export type CourseProgress = {
  status: string;
  progress: number;
};

export type CardReviewData = {
  program: Program | null;
  bloodyEasy: Program | null;
  programNotice: string | null;
  programError: string | null;
  hub: Hub | null;
  programHub: Hub | null;
  hubNotice: string | null;
  hubError: string | null;
  post: WPPost | null;
  attachments: WPMedia[];
  resourceNotice: string | null;
  resourceError: string | null;
  upcoming: AppEvent | null;
  past: AppEvent | null;
  eventNotice: string | null;
  eventError: string | null;
  course: Course | null;
  enrollment: CourseProgress | null;
  courseNotice: string | null;
  courseError: string | null;
  courseSignedOut: boolean;
};

function emptyData(): CardReviewData {
  return {
    program: null,
    bloodyEasy: null,
    programNotice: null,
    programError: null,
    hub: null,
    programHub: null,
    hubNotice: null,
    hubError: null,
    post: null,
    attachments: [],
    resourceNotice: null,
    resourceError: null,
    upcoming: null,
    past: null,
    eventNotice: null,
    eventError: null,
    course: null,
    enrollment: null,
    courseNotice: null,
    courseError: null,
    courseSignedOut: false,
  };
}

function errorMessage(reason: unknown, fallback: string) {
  return reason instanceof Error && reason.message ? reason.message : fallback;
}

function signedOut(reason: unknown) {
  return /401|unauthorized/i.test(errorMessage(reason, ''));
}

function norm(value: string) {
  return decodeHtmlEntities(value).replace(/\s+/g, ' ').trim().toLowerCase();
}

function byTitle<T extends { title: string }>(items: T[], title: string): T | undefined {
  const wanted = norm(title);
  return items.find((item) => norm(item.title) === wanted);
}

function published<T extends { status: string }>(items: T[]): T[] {
  const live = items.filter((item) => item.status === 'PUBLISHED');
  return live.length > 0 ? live : items;
}

function resourceReady(post: WPPost, attachments: WPMedia[]) {
  const excerpt = decodeHtmlEntities(post.excerpt?.rendered || '').trim();
  return Boolean(excerpt) && isUnderRevision(post.acf?.under_revision) && attachments.length > 0;
}

async function loadResource(): Promise<Pick<CardReviewData, 'post' | 'attachments' | 'resourceNotice' | 'resourceError'>> {
  try {
    const named = await wordpressAPI.search(RESOURCE_TITLE, { per_page: 20 });
    const exact = named.find((post) => norm(post.title.rendered) === norm(RESOURCE_TITLE));
    let post = exact ?? null;
    let notice: string | null = null;
    if (!post) {
      const nearby = await wordpressAPI.search('hemorrhage', { per_page: 8 });
      for (const candidate of nearby) {
        const files = await wordpressAPI.fetchAttachments(candidate.id).catch(() => []);
        if (resourceReady(candidate, files)) {
          post = candidate;
          notice = `${RESOURCE_TITLE} was not in the live list. Showing ${decodeHtmlEntities(candidate.title.rendered)}, which has an excerpt, Under revision, and files.`;
          return { post, attachments: files, resourceNotice: notice, resourceError: null };
        }
      }
      post = nearby[0] ?? named[0] ?? null;
      if (!post) {
        return {
          post: null,
          attachments: [],
          resourceNotice: null,
          resourceError: `${RESOURCE_TITLE} was not in the live list, and no other resource was returned.`,
        };
      }
      notice = `${RESOURCE_TITLE} was not in the live list. Showing ${decodeHtmlEntities(post.title.rendered)}.`;
    }
    let attachments: WPMedia[] = [];
    try {
      attachments = await wordpressAPI.fetchAttachments(post.id);
    } catch {
      notice = notice
        ? `${notice} Attachments could not be loaded.`
        : 'Attachments could not be loaded for this document.';
    }
    return { post, attachments, resourceNotice: notice, resourceError: null };
  } catch (reason) {
    return {
      post: null,
      attachments: [],
      resourceNotice: null,
      resourceError: errorMessage(reason, 'Resources could not be loaded.'),
    };
  }
}

export async function loadCardReviewData(): Promise<CardReviewData> {
  const data = emptyData();
  const [programsResult, hubsResult, eventsResult, resourceResult, coursesResult, enrollmentsResult] =
    await Promise.allSettled([
      programAPI.fetchPrograms(),
      hubAPI.fetchHubs(),
      eventAPI.fetchEvents(),
      loadResource(),
      elearningAPI.fetchCourses(),
      elearningAPI.getUserEnrollments(),
    ]);

  if (programsResult.status === 'fulfilled') {
    const programs = published(programsResult.value);
    const exact = byTitle(programs, PROGRAM_TITLE) ?? null;
    data.program = exact ?? programs.find((program) => program.hubs.length > 0) ?? programs[0] ?? null;
    data.bloodyEasy = byTitle(programs, BLOODY_EASY_TITLE) ?? null;
    if (!data.program) {
      data.programError = `${PROGRAM_TITLE} was not in the live list, and no other program was returned.`;
    } else if (!exact) {
      data.programNotice = `${PROGRAM_TITLE} was not in the live list. Showing ${data.program.title}.`;
    }
    if (!data.bloodyEasy && exact) {
      data.programNotice = data.programNotice
        ? `${data.programNotice} ${BLOODY_EASY_TITLE} was not in the live list.`
        : `${BLOODY_EASY_TITLE} was not in the live list, so the “+ N more” specimen is omitted.`;
    }
  } else {
    data.programError = errorMessage(programsResult.reason, 'Programs could not be loaded.');
  }

  if (hubsResult.status === 'fulfilled') {
    const hubs = published(hubsResult.value);
    const exact = byTitle(hubs, HUB_TITLE) ?? null;
    data.hub = exact ?? hubs.find((hub) => hub.resources.length > 0) ?? hubs[0] ?? null;
    data.programHub =
      hubs.find((hub) => norm(hub.title) === norm(PROGRAM_HUB_TITLE) && hub.program) ??
      hubs.find((hub) => norm(hub.title) === norm(PROGRAM_HUB_TITLE)) ??
      null;
    if (!data.hub) {
      data.hubError = `${HUB_TITLE} was not in the live list, and no other hub was returned.`;
    } else if (!exact) {
      data.hubNotice = `${HUB_TITLE} was not in the live list. Showing ${data.hub.title}.`;
    }
    if (!data.programHub) {
      const extra = `${PROGRAM_HUB_TITLE} was not in the live list, so the program-name specimen is omitted.`;
      data.hubNotice = data.hubNotice ? `${data.hubNotice} ${extra}` : extra;
    }
  } else {
    data.hubError = errorMessage(hubsResult.reason, 'Hubs could not be loaded.');
  }

  if (resourceResult.status === 'fulfilled') {
    data.post = resourceResult.value.post;
    data.attachments = resourceResult.value.attachments;
    data.resourceNotice = resourceResult.value.resourceNotice;
    data.resourceError = resourceResult.value.resourceError;
  } else {
    data.resourceError = errorMessage(resourceResult.reason, 'Resources could not be loaded.');
  }

  if (eventsResult.status === 'fulfilled') {
    const events = eventsResult.value;
    const now = Date.now();
    const upcomingExact =
      events.find((event) => norm(event.title) === norm(UPCOMING_TITLE) && new Date(event.startAt).getTime() >= now) ??
      events.find((event) => norm(event.title).includes('october 22, 2026')) ??
      null;
    data.upcoming = upcomingExact ?? events.find((event) => new Date(event.startAt).getTime() >= now) ?? null;
    const pastExact =
      events.find((event) => norm(event.title).includes(norm(PAST_MARK)) && event.recordingUrl) ??
      events.find((event) => norm(event.title).includes(norm(PAST_MARK))) ??
      null;
    data.past =
      pastExact ??
      events.find((event) => new Date(event.startAt).getTime() < now && event.recordingUrl) ??
      events.find((event) => new Date(event.startAt).getTime() < now) ??
      null;
    const notes: string[] = [];
    if (!data.upcoming) notes.push(`${UPCOMING_TITLE} was not in the live list, and no upcoming event was returned.`);
    else if (!upcomingExact) notes.push(`${UPCOMING_TITLE} was not in the live list. Showing ${data.upcoming.title}.`);
    if (!data.past) notes.push('No past event was returned, so the past specimen is omitted.');
    else if (!pastExact) notes.push(`The September 24, 2026 rounds were not in the live list. Showing ${data.past.title}.`);
    else if (!data.past.recordingUrl) notes.push('This past event has no recording, so Watch recording is omitted.');
    data.eventNotice = notes.length > 0 ? notes.join(' ') : null;
  } else {
    data.eventError = errorMessage(eventsResult.reason, 'Events could not be loaded.');
  }

  const coursesSignedOut = coursesResult.status === 'rejected' && signedOut(coursesResult.reason);
  const enrollmentsSignedOut = enrollmentsResult.status === 'rejected' && signedOut(enrollmentsResult.reason);
  if (coursesSignedOut || (coursesResult.status === 'rejected' && enrollmentsSignedOut)) {
    data.courseSignedOut = true;
    data.courseNotice = 'Sign in to load courses.';
  } else if (coursesResult.status === 'fulfilled') {
    const courses = coursesResult.value;
    const exact = byTitle(courses, COURSE_TITLE) ?? null;
    data.course = exact ?? courses[0] ?? null;
    if (!data.course) {
      data.courseError = `${COURSE_TITLE} was not in the live list, and no other course was returned.`;
    } else if (!exact) {
      data.courseNotice = `${COURSE_TITLE} was not in the live list. Showing ${data.course.title}.`;
    }
    if (enrollmentsResult.status === 'fulfilled') {
      const match = enrollmentsResult.value.find((enrollment) => enrollment.courseId === data.course?.id);
      if (match) {
        data.enrollment = { status: match.status, progress: match.progress };
      } else if (data.course) {
        const extra = 'No enrollment was returned for this course, so progress is omitted.';
        data.courseNotice = data.courseNotice ? `${data.courseNotice} ${extra}` : extra;
      }
    } else if (!enrollmentsSignedOut) {
      const extra = 'Enrollments could not be loaded, so progress is omitted.';
      data.courseNotice = data.courseNotice ? `${data.courseNotice} ${extra}` : extra;
    } else {
      data.courseSignedOut = false;
      const extra = 'Sign in to see course progress.';
      data.courseNotice = data.courseNotice ? `${data.courseNotice} ${extra}` : extra;
    }
  } else {
    data.courseError = errorMessage(coursesResult.reason, 'Courses could not be loaded.');
  }

  return data;
}
